import Immutable, { fromJS } from 'immutable';
import { combineReducers } from 'redux-immutable';
import get from 'lodash/get';

import { Message, Session, User, Company, Form, Attachment, Queue, SatisfactionForm } from 'models';

import {
  CHAT_MESSAGE_SEND_OPTIMISTICALLY,
  CHAT_MESSAGE_SENT_SUCCESS,
  CHAT_MESSAGE_SENT_FAILED,
  CHAT_MESSAGE_SENT_LOADING,
  CHAT_MESSAGE_SENT_RETRY,
} from '../../conversation/ChatConversationInput/actions';

import {
  CHAT_UPDATE_SESSION,
  CHAT_MODIFY_SESSION,
  CHAT_UPDATE_SESSIONS,
  CHAT_NEW_SESSION,
  CHAT_UPDATE_SESSION_MESSAGES,
  CHAT_UPDATE_USERS,
  CHAT_UPDATE_COMPANY,
  CHAT_FORM_INIT,
  CHAT_FORM_GET_TEAMS,
  CHAT_FORM_POST,
  CHAT_VOLUME_CONTROL_POST,
  CHAT_FORM_IN_PROGRESS,
  CHAT_ATTACHMENT_FETCH,
  CHAT_QUEUE_FETCH,
  CHAT_QUEUE_POLLING_CLEAR,
  CHAT_MESSAGE_RECEIVED,
  SATISFACTION_FORM,
  GET_KB_ARTICLES,
} from './actions';

const updateSession = (state, session) => {
  const sessionMergeStrategy = (prev, next, key) => {
    // don't overwrite the messages in the session
    return key === 'messages' ? prev : next;
  };

  return state.update(session.id, Session(), s => s.mergeWith(sessionMergeStrategy, session));
};

const sessions = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case CHAT_NEW_SESSION: {
      if (!action.session) return state;
      return updateSession(state, Session(action.session));
    }
    case CHAT_UPDATE_SESSIONS: {
      const { sessions } = action; // eslint-disable-line no-shadow
      if (!sessions) return state;
      return sessions.reduce((s, session) => updateSession(s, Session(session)), state);
    }
    case CHAT_UPDATE_SESSION: {
      const { session } = action; // eslint-disable-line no-shadow
      if (!session) return state;
      return updateSession(state, Session(session));
    }
    case CHAT_MODIFY_SESSION: {
      const { session } = action; // eslint-disable-line no-shadow
      if (!session) return state;
      return state.update(session.id, Session(), s => s.merge(session));
    }
    case CHAT_UPDATE_SESSION_MESSAGES: {
      const { sessionId, messages } = action; // eslint-disable-line no-shadow
      if (!sessionId || !messages || !state.get(sessionId)) return state;
      const newSessionMessages = Immutable.OrderedSet(messages.map(message => message.id));
      return state.updateIn([sessionId, 'messages'], Immutable.OrderedSet(), m => m.merge(newSessionMessages));
    }
    case CHAT_MESSAGE_SEND_OPTIMISTICALLY: {
      const { partialMessage } = action;
      if (!partialMessage || !state.get(partialMessage.sessionId)) return state;

      return state.updateIn([partialMessage.sessionId, 'messages'], Immutable.OrderedSet(), m =>
        m.add(partialMessage.id),
      );
    }
    case CHAT_MESSAGE_SENT_SUCCESS: {
      const { message, partial } = action;
      if (!partial || !message || !state.get(message.sessionId)) return state;

      return state.updateIn([message.sessionId, 'messages'], Immutable.OrderedSet(), m =>
        m.reduce((set, messageId) => {
          return messageId === partial.id ? set.add(message.id) : set.add(messageId);
        }, Immutable.OrderedSet()),
      );
    }
    case CHAT_MESSAGE_RECEIVED: {
      const { message } = action;
      if (!message || !state.get(message.sessionId)) return state;

      return state.updateIn([message.sessionId, 'messages'], Immutable.OrderedSet(), m => m.add(message.id));
    }
    case CHAT_FORM_POST: {
      const session = get(action, 'form.session'); // eslint-disable-line no-shadow
      const messages = get(action, 'form.messages');

      session.messages = Immutable.OrderedSet(messages.map(message => message.id));

      return state.set(session.id, Session(session));
    }
    case CHAT_VOLUME_CONTROL_POST: {
      const session = get(action, 'form.session'); // eslint-disable-line no-shadow
      const messages = get(action, 'form.messages');
      const existingMessages = state.getIn([session.id, 'messages'], Immutable.OrderedSet());

      const validSessionMessages = existingMessages.reduce((set, id) => {
        if (id.length > 20) return set.add(id);
        return set;
      }, Immutable.OrderedSet());

      session.messages = messages.reduce((m, message) => m.add(message.id), validSessionMessages);

      return state.set(session.id, Session(session));
    }
    default: {
      return state;
    }
  }
};

const messages = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case CHAT_UPDATE_SESSION_MESSAGES: {
      const { sessionId, messages } = action; // eslint-disable-line no-shadow
      if (!sessionId || !messages) return state;

      return messages.reduce((s, message) => s.set(message.id, Message(message)), state);
    }
    case CHAT_MESSAGE_SEND_OPTIMISTICALLY: {
      const { partialMessage } = action;
      if (!partialMessage) return state;

      return state.set(partialMessage.id, partialMessage);
    }
    case CHAT_MESSAGE_SENT_SUCCESS: {
      const { message, partial } = action;
      if (!message) return state;
      return state.set(message.id, Message(message)).delete(partial.id);
    }
    case CHAT_MESSAGE_SENT_FAILED: {
      const { partialMessage } = action;
      if (!partialMessage) return state;

      return state.update(partialMessage.id, message => message.set('failed', true));
    }
    case CHAT_MESSAGE_SENT_RETRY: {
      const { partialMessage } = action;
      if (!partialMessage) return state;

      return state.update(partialMessage.id, message => message.set('loading', true).set('failed', false));
    }
    case CHAT_MESSAGE_SENT_LOADING: {
      const { partialMessage } = action;
      if (!partialMessage) return state;

      return state.update(partialMessage.id, message => message.set('loading', true));
    }
    case CHAT_MESSAGE_RECEIVED: {
      const { message } = action;
      if (!message) return state;

      return state.set(message.id, Message(message));
    }
    case CHAT_FORM_POST: {
      const messages = get(action, 'form.messages'); // eslint-disable-line no-shadow
      return messages.reduce((s, message) => s.set(message.id, Message(message)), state);
    }
    case CHAT_VOLUME_CONTROL_POST: {
      const messages = get(action, 'form.messages'); // eslint-disable-line no-shadow
      const existingValid = state.filter(message => message.id && message.id.length > 20);

      return messages.reduce((s, message) => s.set(message.id, Message(message)), existingValid);
    }
    default: {
      return state;
    }
  }
};

const users = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case CHAT_UPDATE_USERS: {
      const { users } = action; // eslint-disable-line no-shadow
      if (!users) return state;

      return users.reduce((u, user) => u.set(user.id, User(user)), state);
    }
    default: {
      return state;
    }
  }
};

export const articles = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case GET_KB_ARTICLES: {
      if (!action.payload) return state;

      return action.payload.reduce((acc, val) => {
        return acc.set(val.id, val);
      }, Immutable.Map());
    }
    default: {
      return state;
    }
  }
};
const company = (state = Company(), action) => {
  switch (action.type) {
    case CHAT_UPDATE_COMPANY: {
      const { company } = action; // eslint-disable-line no-shadow
      if (!company) return state;
      return Company(company);
    }
    default: {
      return state;
    }
  }
};

const forms = (state = Form(), action) => {
  switch (action.type) {
    case CHAT_FORM_INIT: {
      const form = get(action, 'payload.form', {});
      return Form(form);
    }
    case CHAT_FORM_IN_PROGRESS: {
      const inProgress = action.inProgress;
      return state.set('inProgress', inProgress);
    }
    default:
      return state;
  }
};

const teams = (state = Immutable.List(), action) => {
  switch (action.type) {
    case CHAT_FORM_GET_TEAMS: {
      const teamsResp = get(action, 'payload.teams', []);
      return fromJS(teamsResp);
    }
    default:
      return state;
  }
};

const attachments = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case CHAT_ATTACHMENT_FETCH: {
      const messageId = action.messageId;
      const attachment = Attachment(action.attachment);

      return state.set(messageId, attachment);
    }
    default:
      return state;
  }
};

const sessionQueues = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case CHAT_QUEUE_FETCH: {
      const sessionQueue = Queue(action.sessionQueue);
      const queueId = sessionQueue.id;

      return state.set(queueId, sessionQueue);
    }
    case CHAT_QUEUE_POLLING_CLEAR: {
      return state.clear();
    }
    default:
      return state;
  }
};

const satisfactionForms = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case SATISFACTION_FORM: {
      const satisfactionForm = SatisfactionForm(action.satisfactionForm);
      const responseId = satisfactionForm.id;

      return state.set(responseId, satisfactionForm);
    }
    default:
      return state;
  }
};

const entities = combineReducers({
  attachments,
  company,
  forms,
  sessions,
  sessionQueues,
  messages,
  teams,
  users,
  satisfactionForms,
  articles,
});

export default entities;
